// AdminPage.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase/firebaseConfig";
import { Menu, Transition } from "@headlessui/react";
import {
  UserPlus as UserAddIcon,
  Users as UsersIcon,
  Eye as EyeIcon,
  LogOut as LogoutIcon,
  MoreVertical as DotsVerticalIcon,
  CalendarPlus as CalendarPlusIcon,
} from "lucide-react";

import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import {
  Calendar as BigCalendar,
  dateFnsLocalizer,
  Views,
} from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import cs from "date-fns/locale/cs";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import dayjs from "dayjs";
import ToastMessage from "../components/ToastMessage";
import Modal from "../components/Modal";

const locales = { cs };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const customFormats = {
  timeGutterFormat: (date) => dayjs(date).format("HH:mm"),
  eventTimeRangeFormat: ({ start, end }) => `${dayjs(start).format("HH:mm")} - ${dayjs(end).format("HH:mm")}`,
  dayFormat: (date) => dayjs(date).format("dd DD.MM."),
  weekdayFormat: (date) => dayjs(date).format("dddd"),
  agendaDateFormat: (date) => dayjs(date).format("D. MMMM YYYY"),
  agendaTimeFormat: (date) => dayjs(date).format("HH:mm"),
  agendaTimeRangeFormat: ({ start, end }) => `${dayjs(start).format("HH:mm")} - ${dayjs(end).format("HH:mm")}`,
  agendaHeaderFormat: ({ start, end }) => `${dayjs(start).format("D. MMMM")} – ${dayjs(end).format("D. MMMM")}`,
  dayRangeHeaderFormat: ({ start, end }) => `${dayjs(start).format("D. MMMM")} – ${dayjs(end).format("D. MMMM")}`,
  dayHeaderFormat: (date) => dayjs(date).format("dddd D. MMMM"),
  monthHeaderFormat: (date) => dayjs(date).format("MMMM YYYY"),
};
const DnDCalendar = withDragAndDrop(BigCalendar);

const AdminPage = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [toastType, setToastType] = useState("success");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [calendarView, setCalendarView] = useState(Views.DAY);
  const [calendarDate, setCalendarDate] = useState(new Date());

  const showToast = (msg, type = "success") => {
    setToastType(type);
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const fetchEvents = useCallback(async () => {
    try {
      const q = query(collection(db, "events"));
      const snapshot = await getDocs(q);
      const fetchedEvents = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.eventType || "Oběd",
          start: new Date(data.startTime.seconds * 1000),
          end: new Date(data.endTime.seconds * 1000),
          description: data.description || "",
          eventType: data.eventType || "Oběd",
          organizer: data.organizer,
        };
      });
      setEvents(fetchedEvents);
    } catch (error) {
      console.error("Chyba při načítání událostí:", error);
      showToast("Chyba při načítání událostí.", "error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const email = localStorage.getItem("userEmail");
    if (!email) return navigate("/login");
    const getUser = async () => {
      try {
        const q = query(collection(db, "users"), where("email", "==", email));
        const snapshot = await getDocs(q);
        const user = snapshot.docs[0]?.data();
        setUserName(user?.name || email || "Admin");
      } catch {
        setUserName(email);
      }
    };
    getUser();
    fetchEvents();
  }, [fetchEvents, navigate]);

  const addLunchEvents = async () => {
    try {
      const q = query(collection(db, "events"));
      const snapshot = await getDocs(q);
      const existingEvents = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        start: new Date(doc.data().startTime.seconds * 1000),
        end: new Date(doc.data().endTime.seconds * 1000),
      }));

      const startOfWeekDate = dayjs().startOf("week").add(1, "day");
      const daysToAdd = [];

      for (let i = 0; i < 5; i++) {
        const day = startOfWeekDate.add(i, "day");
        const start = day.hour(11).minute(30).second(0).millisecond(0).toDate();
        const end = day.hour(12).minute(30).second(0).millisecond(0).toDate();

        const hasConflict = existingEvents.some((ev) => dayjs(ev.start).isBefore(end) && dayjs(ev.end).isAfter(start));
        const alreadyHasLunch = existingEvents.some((ev) => ev.eventType === "Oběd" && ev.organizer === "Systém" && dayjs(ev.start).isSame(start, "day"));

        if (!hasConflict && !alreadyHasLunch) {
          daysToAdd.push({ start, end });
        }
      }

      if (daysToAdd.length === 0) {
        showToast("Obědy už jsou přidány nebo časy kolidují.", "info");
        return;
      }

      await Promise.all(
        daysToAdd.map(({ start, end }) =>
          addDoc(collection(db, "events"), {
            eventType: "Oběd",
            description: "Blokace času na oběd",
            startTime: Timestamp.fromDate(start),
            endTime: Timestamp.fromDate(end),
            organizer: "Systém",
          })
        )
      );

      await fetchEvents();
      showToast("Obědové události přidány.");
    } catch (err) {
      console.error("Chyba při přidávání obědů:", err);
      showToast("Nepodařilo se přidat obědy.", "error");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.clear();
    navigate("/");
  };

  const handleSelectSlot = ({ start, end }) => {
    setSelectedEvent({
      start,
      end: end || dayjs(start).add(1, "hour").toDate(),
      eventType: "Meeting",
      description: "",
      title: "",
      organizer: userName,
    });
    setModalOpen(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent({ ...event, start: new Date(event.start), end: new Date(event.end) });
    setModalOpen(true);
  };

  const confirmOverlap = (suggestStart, suggestEnd, data) => {
    setConfirmData({
      message: `Zadaný čas koliduje. Navrhuji ${dayjs(suggestStart).format("HH:mm")}-${dayjs(suggestEnd).format("HH:mm")}. Použít?`,
      onConfirm: () => {
        data.start = suggestStart;
        data.end = suggestEnd;
        setConfirmData(null);
        handleSaveEvent(data);
      },
      onCancel: () => setConfirmData(null),
    });
  };

  const handleSaveEvent = async (data) => {
    const snapshot = await getDocs(query(collection(db, "events")));
    const overlap = snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .find((e) => {
        if (data.id && e.id === data.id) return false;
        const existingStart = dayjs(e.startTime?.seconds * 1000);
        const existingEnd = dayjs(e.endTime?.seconds * 1000);
        return dayjs(data.start).isBefore(existingEnd) && dayjs(data.end).isAfter(existingStart);
      });

    if (overlap) {
      const suggestStart = dayjs(overlap.endTime.seconds * 1000).add(1, "minute").toDate();
      const suggestEnd = dayjs(suggestStart).add(dayjs(data.end).diff(dayjs(data.start), "minute"), "minute").toDate();
      return confirmOverlap(suggestStart, suggestEnd, data);
    }

    try {
      const payload = {
        eventType: data.eventType,
        description: data.description,
        startTime: Timestamp.fromDate(data.start),
        endTime: Timestamp.fromDate(data.end),
        organizer: data.organizer || userName,
      };

      if (data.id) {
        await updateDoc(doc(db, "events", data.id), payload);
        showToast("Událost upravena.");
      } else {
        await addDoc(collection(db, "events"), payload);
        showToast("Událost přidána.");
      }
      setModalOpen(false);
      fetchEvents();
    } catch (err) {
      console.error("Chyba:", err);
      showToast("Chyba při ukládání události.", "error");
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await deleteDoc(doc(db, "events", id));
      showToast("Událost smazána.");
      setModalOpen(false);
      fetchEvents();
    } catch (err) {
      console.error("Mazání selhalo:", err);
      showToast("Nepodařilo se smazat událost.", "error");
    }
  };

  const moveEvent = ({ event, start, end }) => handleSaveEvent({ ...event, start, end });
  const handleViewChange = (view) => setCalendarView(view);
  const handleNavigate = (date) => setCalendarDate(date);
  if (isLoading) return <div className="p-8 text-center">Načítám...</div>;

  return (
    <div className="h-screen flex flex-col bg-gray-100 p-6">
      {toast && <ToastMessage message={toast} type={toastType} onClose={() => setToast("")} />}
      {confirmData && (
        <div style={{ position: "fixed", zIndex: 1001, inset: 0 }}>
          <ToastMessage message={confirmData.message} type="confirm" onConfirm={confirmData.onConfirm} onCancel={confirmData.onCancel} />
        </div>
      )}
      <header className="flex flex-wrap justify-between items-start gap-3 mb-6">
  <h1 className="text-xl font-bold">Zasedací místnost – admin ({userName})</h1>

  {/* Desktop buttons */}
  <div className="hidden md:flex gap-3">
    <button onClick={() => navigate("/register")} className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-1">
      <UserAddIcon className="w-5 h-5" /> Nový
    </button>
    <button onClick={() => navigate("/users")} className="bg-purple-600 text-white px-4 py-2 rounded flex items-center gap-1">
      <UsersIcon className="w-5 h-5" /> Uživatelé
    </button>
    <button onClick={() => window.open("/", "_blank")} className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-1">
      <EyeIcon className="w-5 h-5" /> Náhled
    </button>
    <button onClick={addLunchEvents} className="bg-yellow-500 text-white px-4 py-2 rounded flex items-center gap-1">
      <CalendarPlusIcon className="w-5 h-5" /> Obědy
    </button>
    <button onClick={handleLogout} className="bg-red-600 text-white px-4 py-2 rounded flex items-center gap-1">
      <LogoutIcon className="w-5 h-5" /> Odhlásit
    </button>
  </div>

  {/* Mobile dropdown menu */}
  <div className="md:hidden flex justify-end w-full">
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="inline-flex justify-center rounded bg-gray-200 p-2 hover:bg-gray-300">
          <DotsVerticalIcon className="h-5 w-5 text-gray-700" />
        </Menu.Button>
      </div>

      <Transition
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => navigate("/register")}
                  className={`${active ? "bg-green-100 text-green-900" : "text-gray-900"} flex w-full items-center px-4 py-2 text-sm`}
                >
                  <UserAddIcon className="mr-2 h-4 w-4" /> Nový
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => navigate("/users")}
                  className={`${active ? "bg-purple-100 text-purple-900" : "text-gray-900"} flex w-full items-center px-4 py-2 text-sm`}
                >
                  <UsersIcon className="mr-2 h-4 w-4" /> Uživatelé
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => window.open("/", "_blank")}
                  className={`${active ? "bg-blue-100 text-blue-900" : "text-gray-900"} flex w-full items-center px-4 py-2 text-sm`}
                >
                  <EyeIcon className="mr-2 h-4 w-4" /> Náhled
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={addLunchEvents}
                  className={`${active ? "bg-yellow-100 text-yellow-900" : "text-gray-900"} flex w-full items-center px-4 py-2 text-sm`}
                >
                  <CalendarPlusIcon className="mr-2 h-4 w-4" /> Obědy
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleLogout}
                  className={`${active ? "bg-red-100 text-red-900" : "text-gray-900"} flex w-full items-center px-4 py-2 text-sm`}
                >
                  <LogoutIcon className="mr-2 h-4 w-4" /> Odhlásit
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  </div>
</header>
      <div className="w-full flex-1 overflow-hidden">
        <DnDCalendar
          formats={customFormats}
          localizer={localizer}
          events={events}
          defaultView={Views.DAY}
          view={calendarView}
          date={calendarDate}
          onView={handleViewChange}
          onNavigate={handleNavigate}
          startAccessor="start"
          endAccessor="end"
          selectable
          style={{ height: "100", width: "100%" }}
          className="bg-white rounded shadow p-2"
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          onEventDrop={moveEvent}
          onEventResize={moveEvent}
          resizable
          popup
          views={Object.values(Views)}
          messages={{
            week: "Týden",
            work_week: "Pracovní týden",
            day: "Den",
            month: "Měsíc",
            previous: "Předchozí",
            next: "Další",
            today: "Dnes",
            agenda: "Agenda",
            date: "Datum",
            time: "Čas",
            event: "Událost",
            noEventsInRange: "Žádné události v tomto rozsahu.",
            showMore: (total) => `+${total} více`,
          }}
        />
      </div>
      {modalOpen && selectedEvent && (
        <Modal eventData={selectedEvent} onClose={() => setModalOpen(false)} onSave={handleSaveEvent} onDelete={handleDeleteEvent} />
      )}
    </div>
  );
};

export default AdminPage;
